import React, { useContext, useLayoutEffect, useState } from 'react';
import LocaleContext from '../../context/Locale';
import { MenuItem } from '../MenuItem';
import styled, { css } from 'styled-components';
import Logo from '../../../static/icons/logo-nav.svg';
import PhoneIcon from '../../../static/icons/call_black_24dp.svg';
import EmailIcon from '../../../static/icons/email_black_24dp.svg';
import ScheduleIcon from '../../../static/icons/schedule_black_24dp.svg';
import { Container } from '../Container/Container';
import { graphql, Link, useStaticQuery } from 'gatsby';
import { pxToRem } from '../../utils/pxToRem';
import { easeInOut, rem } from 'polished';
import { MenuMobile } from '../MenuMobile/MenuMobile';
import { PageRow } from '../PageRow/PageRow';
import { useLocation } from '@reach/router';
import { ROUTES } from '../../utils/routes';
import { Query } from '../../model/generated/graphql';

const variants = {
    open: {
        display: 'block',
        transition: { staggerChildren: 0.07, delayChildren: 0.2 },
    },
    closed: {
        transition: {
            staggerChildren: 0.05,
            staggerDirection: -1,
        },
        display: 'none',
    },
};

const menu = {
    open: { opacity: 1 },
    closed: { opacity: 0 },
};

export const Navigation = () => {
    const { activeLocale, updateLocale } = useContext(LocaleContext);
    const { contentfulPageKontakt: contact }: Query = useStaticQuery(graphql`
        query ContactHookQuery($node_locale: String) {
            contentfulPageKontakt(contentful_id: { eq: "4CI60KPsDdc3LGC7S3xH9k" }, node_locale: { eq: $node_locale }) {
                title
                companyName
                city
                street
                zipCode
                email
                phone1Label
                phone1Number
                phone2Label
                phone2Number
                phone3Label
                phone3Number
                openingHoursLabel
                openingHoursValue
                location {
                    lat
                    lon
                }
            }
        }
    `);

    const { pathname } = useLocation();

    const [open, toogleOpen] = useState(false);
    const [menuSticky, setMenuSticky] = useState(false);

    useLayoutEffect(() => {
        let ticking = false;

        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(() => {
                const shouldStick = window.scrollY > 100;
                // Only update when the value actually changes to avoid a
                // re-render -> style-injection -> scrollY-read reflow loop.
                setMenuSticky((prev) => (prev === shouldStick ? prev : shouldStick));
                ticking = false;
            });
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <header>
            <NavWrapper>
                <Topbar>
                    <Container layout={{ display: 'flex', justifyContent: 'flex-end' }} mobile>
                        <TopbarItemWrapper>
                            <TopbarButton
                                className="btn-mydr-pp"
                                data-doctor=""
                                data-speciality=""
                                data-visitkind=""
                                data-evisit="false"
                                data-appname="drw"
                                data-token="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJmYWNpbGl0eV9pZCI6ODU3N30.2LCjgzbBITl06kc5exgkLYg7E8n0V-quZZMO5ozn8hw"
                                style={{
                                    width: 'unset',
                                    height: 'unset',
                                    background: 'none',
                                    color: 'white',
                                    outline: 'none',
                                    border: 'none',
                                    fontWeight: 500,
                                }}
                            >
                                Umów wizytę
                            </TopbarButton>
                        </TopbarItemWrapper>
                        <TopbarItemWrapper hideMobile={true}>
                            <TopbarItem>
                                <ScheduleIcon width={18} fill="white" aria-hidden="true" /> {contact.openingHoursValue}
                            </TopbarItem>
                        </TopbarItemWrapper>
                        <TopbarItemWrapper>
                            <TopbarLink href={`mailto:${contact.email}`}>
                                <EmailIcon width={18} fill="white" aria-hidden="true" />
                                <HideLabelOnMobile>{contact.email}</HideLabelOnMobile>
                            </TopbarLink>
                        </TopbarItemWrapper>
                        <TopbarItemWrapper>
                            <PhoneLink href={`tel:+48${contact.phone1Number.split(' ').join('')}`}>
                                <PhoneIcon width={18} fill="white" aria-hidden="true" />
                                {contact.phone1Number}
                            </PhoneLink>
                        </TopbarItemWrapper>
                    </Container>
                </Topbar>
                <MainMenu sticky={menuSticky}>
                    <Container>
                        <PageRow>
                            <Navbar>
                                <LogoWrapper sticky={menuSticky}>
                                    <Link to={'/'} aria-label="Strona główna MAGMED">
                                        <Logo width={250} aria-hidden="true" />
                                    </Link>
                                </LogoWrapper>

                                <nav aria-label="Główna nawigacja">
                                    <NavList>
                                        {itemIds.map((i) => (
                                            <MenuItem item={i} key={i.name} active={pathname.includes(i.slug)} />
                                        ))}
                                    </NavList>
                                </nav>
                            </Navbar>
                        </PageRow>
                    </Container>
                </MainMenu>
            </NavWrapper>
            <MenuMobile open={open} itemIds={itemIds} />
        </header>
    );
};

const NavWrapper = styled.div`
    position: fixed;
    top: 0;
    z-index: 9998;
    width: 100%;
    padding-right: var(--scrollbar-compensation, 0px);
    transition: padding-right 0ms;

    @media (max-width: ${pxToRem(1000)}) {
        position: relative;
        padding-right: 0;
        padding-top: ${pxToRem(48)};
    }
`;

const MainMenu = styled.div<{ sticky: boolean }>`
    width: 100%;
    padding: ${(props) => (props.sticky ? `${rem(2)} 0` : `${rem(14)} 0`)};
    background-color: ${(props) => (props.sticky ? 'rgba(255, 255, 255, 0.9)' : 'white')};
    backdrop-filter: ${(props) => (props.sticky ? 'blur(12px)' : 'none')};
    -webkit-backdrop-filter: ${(props) => (props.sticky ? 'blur(12px)' : 'none')};
    box-shadow: ${(props) =>
        props.sticky
            ? '0 2px 24px rgba(0, 0, 0, 0.13), 0 1px 4px rgba(0, 0, 0, 0.08)'
            : '0 2px 8px rgba(0, 0, 0, 0.06)'};
    transition: padding 350ms cubic-bezier(0.4, 0, 0.2, 1), background-color 350ms ease, box-shadow 350ms ease;

    @media (max-width: ${pxToRem(1000)}) {
        padding: 0;
        background-color: white;
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
        box-shadow: none;
    }
`;

const Topbar = styled.div`
    width: 100%;
    min-height: ${pxToRem(48)};
    background-color: var(--color-primary);
    color: var(--color-white);
    display: flex;
    align-content: flex-end;
    padding: ${pxToRem(3)};
    box-sizing: border-box;

    @media (max-width: ${pxToRem(1000)}) {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 9998;
    }
`;

const BooksyLabel = styled.div`
    @media (max-width: ${pxToRem(700)}) {
        display: none;
    }
`;

const pillStyles = css`
    border-radius: 2rem;
    margin: ${rem(4)} ${rem(4)};
    padding: ${rem(5)} ${rem(14)};
`;

const TopbarItemStyles = css`
    font-size: 0.8rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    color: var(--color-white);
    padding: ${rem(8)} ${rem(12)};
    & svg {
        margin-right: ${pxToRem(6)};
    }
`;

export const TopbarLink = styled.a`
    ${TopbarItemStyles};
    ${pillStyles};
    background-color: transparent;
    text-decoration: none;
    transition: background-color ${easeInOut('quad')} 300ms;
    :hover {
        background-color: var(--color-secondary);
    }
    :focus-visible {
        outline: 2px solid var(--color-white);
        outline-offset: 2px;
    }
`;

const TopbarButton = styled.button`
    ${TopbarItemStyles};
    ${pillStyles};
    background-color: transparent;
    border: none;
    cursor: pointer;
    transition: background-color ${easeInOut('quad')} 300ms;
    :hover {
        background-color: var(--color-secondary) !important;
    }
    :focus-visible {
        outline: 2px solid var(--color-white);
        outline-offset: 2px;
    }
`;

const PhoneLink = styled(TopbarLink)`
    background-color: var(--color-secondary);
    font-weight: 700;
    letter-spacing: 0.01em;
    :hover {
        background-color: var(--color-secondary-dark);
    }
`;

const HideLabelOnMobile = styled.span`
    @media (max-width: 600px) {
        position: absolute !important;
        height: 1px;
        width: 1px;
        overflow: hidden;
        clip-path: inset(50%);
        white-space: nowrap;
        left: -9999px;
    }
`;

const TopbarItem = styled.span`
    ${TopbarItemStyles}
`;

type TopbarItemWrapperProps = {
    hideMobile?: boolean;
};

const TopbarItemWrapper = styled.div<TopbarItemWrapperProps>`
    display: inline-flex;
    ${(props) => {
        if (props.hideMobile) {
            return css`
                @media (max-width: 800px) {
                    display: none;
                }
            `;
        }
    }}
`;

const Navbar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const LogoWrapper = styled.div<{ sticky: boolean }>`
    transform: scale(${(props) => (props.sticky ? 0.7 : 1)});
    transform-origin: left center;
    transition: transform 350ms cubic-bezier(0.4, 0, 0.2, 1);
    will-change: transform;
    display: block;
`;

const NavList = styled.ul`
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: ${rem(20)};
    margin: 0;

    @media (max-width: ${rem(1000)}) {
        display: none;
    }
`;

export type IMenuItem = {
    name: string;
    slug: string;
};

const itemIds = [
    {
        name: 'Aktualności',
        slug: ROUTES.NEWS,
    },
    {
        name: 'Specjaliści',
        slug: ROUTES.TEAM,
    },
    {
        name: 'Rehabilitacja',
        slug: ROUTES.REHABILITACJA,
    },
    {
        name: 'Cennik',
        slug: ROUTES.CENNIK,
    },

    {
        name: 'Kontakt',
        slug: ROUTES.KONTAKT,
    },
];
